import React, { useState, useCallback } from 'react';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import DeleteIcon from '@material-ui/icons/Delete';
import { useSnackbar } from 'notistack';

import { SectionHeading, Text } from '../../_core/_ui/typography.component';
import { PrimaryButton, SecondaryButton } from '../../_core/_ui/buttons.component';
import { DashboardCard } from '../../_core/_ui/dashboard-card.component';
import { ParentVerifyContactContainer } from '../../User/ParentVerifyContactForm/parent-verify-contact.container';
import { formatPhone } from '../../../lib/helpers/phone.helpers';
import {
  useParentSetDefaultEmail,
  useParentSetDefaultPhone,
  useParentRemoveEmail,
  useParentRemovePhone,
} from '../../../lib/services/parent.service';
import { IParentSelf } from '../../../lib/types';
import {
  CredentialList,
  CredentialRow,
  CredentialValue,
  DefaultTag,
} from './parent-account.styles';

interface IParentVerifiedCredentialsProps {
  parent: IParentSelf;
}

// One bullet per verified email/phone; array[0] is the default. The star
// icon dispatches the reorder mutation to promote any entry to default.
export const ParentVerifiedCredentials: React.FC<IParentVerifiedCredentialsProps> = ({ parent }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const closeDialog = useCallback(() => setDialogOpen(false), []);

  const [setDefaultEmail, { loading: settingEmail }] = useParentSetDefaultEmail();
  const [setDefaultPhone, { loading: settingPhone }] = useParentSetDefaultPhone();
  const [removeEmail, { loading: removingEmail }] = useParentRemoveEmail();
  const [removePhone, { loading: removingPhone }] = useParentRemovePhone();
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // Floor: at least one verified credential must remain (sign-in surface).
  const isLastCredential = (parent.emails.length + parent.phones.length) <= 1;

  const promoteEmail = useCallback(async (value: string) => {
    setPendingValue(value);
    try {
      await setDefaultEmail({ variables: { value } });
    } finally {
      setPendingValue(null);
    }
  }, [setDefaultEmail]);
  const promotePhone = useCallback(async (value: string) => {
    setPendingValue(value);
    try {
      await setDefaultPhone({ variables: { value } });
    } finally {
      setPendingValue(null);
    }
  }, [setDefaultPhone]);

  const handleRemoveEmail = useCallback(async (value: string) => {
    if (!window.confirm(`Remove ${value} from your verified emails? Players currently using this email keep it; you can change them from "Per-player contact details" below.`)) return;
    setPendingValue(value);
    try {
      await removeEmail({ variables: { value } });
      enqueueSnackbar(`Removed ${value}.`, { variant: 'success' });
    } catch (err) {
      console.error('parentRemoveEmail failed:', err);
      enqueueSnackbar(err.message || 'Could not remove that email. Please try again.', { variant: 'error' });
    } finally {
      setPendingValue(null);
    }
  }, [removeEmail, enqueueSnackbar]);
  const handleRemovePhone = useCallback(async (value: string) => {
    const display = formatPhone(value);
    if (!window.confirm(`Remove ${display} from your verified phones? Players currently using this phone keep it; you can change them from "Per-player contact details" below.`)) return;
    setPendingValue(value);
    try {
      await removePhone({ variables: { value } });
      enqueueSnackbar(`Removed ${display}.`, { variant: 'success' });
    } catch (err) {
      console.error('parentRemovePhone failed:', err);
      enqueueSnackbar(err.message || 'Could not remove that phone. Please try again.', { variant: 'error' });
    } finally {
      setPendingValue(null);
    }
  }, [removePhone, enqueueSnackbar]);

  return (
    <>
      <DashboardCard>
        <Box>
          <SectionHeading>Verified emails</SectionHeading>
          {parent.emails.length ? (
            <CredentialList aria-label="Verified emails">
              {parent.emails.map((email, idx) => {
                const isDefault = idx === 0;
                const promoting = settingEmail && pendingValue === email;
                const removing = removingEmail && pendingValue === email;
                return (
                  <CredentialRow key={email}>
                    <CredentialValue>{email}</CredentialValue>
                    {isDefault && <DefaultTag>Default</DefaultTag>}
                    <Tooltip title={isDefault ? 'Default email' : 'Set as default email'}>
                      <span>
                        <IconButton
                          aria-label={isDefault ? `${email} is the default email` : `Set ${email} as default email`}
                          size="small"
                          disabled={isDefault || promoting || removing}
                          onClick={() => promoteEmail(email)}
                        >
                          {promoting ? <CircularProgress size={16} /> : (isDefault ? <StarIcon /> : <StarBorderIcon />)}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isLastCredential ? 'Add another email or phone before removing this one' : `Remove ${email}`}>
                      <span>
                        <IconButton
                          aria-label={`Remove ${email}`}
                          size="small"
                          disabled={isLastCredential || promoting || removing}
                          onClick={() => handleRemoveEmail(email)}
                        >
                          {removing ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </CredentialRow>
                );
              })}
            </CredentialList>
          ) : (
            <Text>None yet.</Text>
          )}
        </Box>

        <Box mt={3}>
          <SectionHeading>Verified phone numbers</SectionHeading>
          {parent.phones.length ? (
            <CredentialList aria-label="Verified phone numbers">
              {parent.phones.map((phone, idx) => {
                const isDefault = idx === 0;
                const display = formatPhone(phone);
                const promoting = settingPhone && pendingValue === phone;
                const removing = removingPhone && pendingValue === phone;
                return (
                  <CredentialRow key={phone}>
                    <CredentialValue>{display}</CredentialValue>
                    {isDefault && <DefaultTag>Default</DefaultTag>}
                    <Tooltip title={isDefault ? 'Default phone' : 'Set as default phone'}>
                      <span>
                        <IconButton
                          aria-label={isDefault ? `${display} is the default phone` : `Set ${display} as default phone`}
                          size="small"
                          disabled={isDefault || promoting || removing}
                          onClick={() => promotePhone(phone)}
                        >
                          {promoting ? <CircularProgress size={16} /> : (isDefault ? <StarIcon /> : <StarBorderIcon />)}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isLastCredential ? 'Add another email or phone before removing this one' : `Remove ${display}`}>
                      <span>
                        <IconButton
                          aria-label={`Remove ${display}`}
                          size="small"
                          disabled={isLastCredential || promoting || removing}
                          onClick={() => handleRemovePhone(phone)}
                        >
                          {removing ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </CredentialRow>
                );
              })}
            </CredentialList>
          ) : (
            <Text>None yet.</Text>
          )}
        </Box>

        <Box mt={4}>
          <PrimaryButton variant="contained" onClick={() => setDialogOpen(true)}>
            + Verify another email or phone
          </PrimaryButton>
        </Box>
      </DashboardCard>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        aria-label="Add another email or phone"
      >
        <DialogContent>
          <ParentVerifyContactContainer onSuccess={closeDialog} />
        </DialogContent>
        <DialogActions>
          <SecondaryButton variant="text" onClick={closeDialog}>Cancel</SecondaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
